<script setup lang="ts">
import { stagger, useAnimate, useInView, useMotionValue, useTransform } from 'motion-v'

const timelineItems = [
  'Spotting your quiet nights...',
  'Looking at your content...',
  'Creating marketing campaigns...',
]

const dates = [23, 24, 25, 26]
const times = ['9 AM', '12 AM', '3 PM', '6 PM', '9 PM', '12 AM']

type CardIcon = 'email' | 'social'
type CardLabel = 'EMAIL' | 'REEL' | 'POST' | 'STORY'

interface Card {
  image: string
  title: string
  label: CardLabel
  icon: CardIcon
  col: number
  rowStart: number
  rowEnd: number
}

const cards: Card[] = [
  { image: 'private_party.png', title: 'Private Party', label: 'EMAIL', icon: 'email', col: 1, rowStart: 2, rowEnd: 4 },
  { image: '5_star_review.png', title: '5-Star Review', label: 'STORY', icon: 'social', col: 1, rowStart: 6, rowEnd: 8 },
  { image: 'catering.png', title: 'Catering', label: 'REEL', icon: 'social', col: 2, rowStart: 2, rowEnd: 4 },
  { image: 'happy_hour.png', title: 'Happy Hour', label: 'POST', icon: 'social', col: 2, rowStart: 5, rowEnd: 7 },
  { image: 'new_menu_item.png', title: 'New Menu Item', label: 'POST', icon: 'social', col: 3, rowStart: 4, rowEnd: 6 },
  { image: 'online_orders.png', title: 'Online Orders', label: 'EMAIL', icon: 'email', col: 4, rowStart: 2, rowEnd: 4 },
  { image: 'saturday_live_music.png', title: 'Live Event', label: 'STORY', icon: 'social', col: 4, rowStart: 5, rowEnd: 7 },
]

const baseImageUrl = `${S3_ASSETS_URL}/home/always-remembered`

const isScreenMd = useMediaQuery('(min-width: 768px)')
const rowHeight = computed(() => isScreenMd.value ? '3.35rem' : '2.5rem')

const activeTimelineItemIndex = ref(0)

const timelineIndex = useMotionValue(0)
const roundTimelineIndex = useTransform(timelineIndex, Math.round)

const [scope, animate] = useAnimate()
const isInView = useInView(scope, {
  amount: 0.3,
})

roundTimelineIndex.on('change', (latest) => {
  activeTimelineItemIndex.value = latest
})

let hasPlayed = false

watch(isInView, async (inView) => {
  if (!inView || hasPlayed) return
  hasPlayed = true

  timelineIndex.set(0)

  await animate([
    [
      '[data-id=list-group]',
      { opacity: 1, display: 'block' },
      { duration: 0 },
    ],
    [
      '[data-id=calendar]',
      { opacity: 0, scale: 0.95, display: 'none' },
      { duration: 0 },
    ],
    [
      '[data-id=card]',
      { opacity: 0, scale: 0.85, y: 8 },
      { duration: 0 },
    ],
  ]).finished

  await animate([
    [
      timelineIndex,
      2,
      { duration: 1.6 },
    ],
    [
      '[data-id=list-group]',
      { opacity: 0, display: 'none' },
    ],
    [
      '[data-id=calendar]',
      { opacity: 1, scale: 1, display: 'block' },
    ],
    [
      '[data-id=card]',
      { opacity: 1, scale: 1, y: 0 },
      { delay: stagger(0.18), duration: 0.3, ease: [0.2, 0.8, 0.2, 1] },
    ],
  ]).finished
})
</script>

<template>
  <div
    ref="scope"
    class="flex h-96 items-center justify-center place-self-center"
  >
    <div
      data-id="list-group"
      class="rounded-2xl bg-white p-4 shadow ring-1 ring-neutral-200 md:p-8"
    >
      <header class="flex items-center gap-x-2">
        <UiIcon
          name="magnifier-star-outline"
          class="text-2xl"
        />

        <span class="text-lg font-semibold md:text-xl">AI Working On</span>
      </header>

      <ul class="isolate mt-5">
        <li
          v-for="(timelineItem, timelineItemIndex) in timelineItems"
          :key="timelineItemIndex"
          class="group grid grid-cols-[min-content_1fr] gap-x-4"
          :data-id="`timeline-${timelineItemIndex}`"
          :data-state="timelineItemIndex === activeTimelineItemIndex ? 'active' : 'inactive'"
        >
          <div
            :class="[
              'relative before:relative before:inline-flex before:size-3 before:rounded-[50%] before:bg-neutral-200 before:text-xl before:font-semibold before:text-white before:transition-all after:absolute after:left-1/2 after:top-3 after:z-[-1] after:h-full after:w-[0.125rem] after:-translate-x-1/2 after:bg-gradient-to-b after:from-neutral-200 after:to-neutral-200 after:transition-all group-data-[state=active]:before:!bg-orange-500 group-data-[state=active]:before:ring-4 group-data-[state=active]:before:ring-orange-400/30',
              {
                'after:hidden': timelineItemIndex === timelineItems.length - 1,
                'before:!bg-orange-200 after:!from-orange-200 after:!to-orange-200': activeTimelineItemIndex !== timelineItemIndex && (activeTimelineItemIndex > timelineItemIndex),
                'after:!from-orange-200 after:!to-orange-500': activeTimelineItemIndex === timelineItemIndex + 1
              }
            ]"
          />

          <div
            :class="['w-56 py-1 text-xs leading-none transition-all group-data-[state=active]:font-bold group-data-[state=active]:text-orange-500 md:w-72 md:py-0 md:text-base', {
              'mb-10': Number(timelineItemIndex) !== timelineItems.length - 1
            }]"
          >
            {{ timelineItem }}
          </div>
        </li>
      </ul>
    </div>

    <div
      data-id="calendar"
      class="mx-auto w-full max-w-md place-self-center lg:max-w-none"
      :style="{
        opacity: 0,
        display: 'none',
      }"
    >
      <div class="rounded-2xl bg-white p-3 shadow ring-1 ring-neutral-200 md:p-5  !pt-0 !pr-0 !pb-0">
        <div
          class="relative grid"
          :style="{
            gridTemplateColumns: `minmax(1.5rem, max-content) repeat(${dates.length}, minmax(0, 1fr))`,
            gridTemplateRows: `auto repeat(${times.length}, ${rowHeight})`,
          }"
        >
          <div />

          <div
            v-for="date in dates"
            :key="date"
            class="pb-3 text-center text-sm pt-3 text-neutral-400 border-l border-neutral-200"
          >
            {{ date }}
          </div>

          <div
            v-for="(time, timeIndex) in times"
            :key="time"
            :style="{ gridColumn: 1, gridRow: timeIndex + 2 }"
            class="-translate-y-[0.3rem] whitespace-nowrap pr-0.5 text-right text-[7.2px] text-neutral-400 md:pr-0.5 md:text-[7.2px]"
          >
            {{ time }}
          </div>

          <template
            v-for="row in times.length"
            :key="`row-${row}`"
          >
            <div
              v-for="col in dates.length"
              :key="`cell-${row}-${col}`"
              :style="{ gridColumn: col + 1, gridRow: row + 1 }"
              class="border-l border-t border-neutral-200 [border-top-style:dashed]"
            />
          </template>

          <div
            v-for="card in cards"
            :key="card.title"
            data-id="card"
            :style="{
              gridColumn: card.col + 1,
              gridRow: `${card.rowStart} / ${card.rowEnd}`,
              opacity: 0,
            }"
            class="relative z-10 mx-1 !ml-[0.35rem] self-start overflow-hidden rounded-[7.2px] bg-white shadow-md ring-1 ring-neutral-200"
            :class="{ '-mt-[30px]': card.title === '5-Star Review', 'mt-[15px]': card.title === 'Catering', '-mt-[10px]': card.title === 'Happy Hour', 'mt-[20px]': card.title === 'Online Orders' }"
          >
            <div class="relative">
              <img
                :src="`${baseImageUrl}/${card.image}`"
                :alt="card.title"
                class="block w-full object-contain"
                draggable="false"
              >

              <span class="absolute bottom-1 right-1 z-10 rounded-lg bg-white/40 px-0.5 py-0.5 text-[0.5rem] font-semibold uppercase tracking-wider text-neutral-700 md:-bottom-2.5 md:px-1 md:py-1">
                <span class="inline-flex items-center gap-x-1 rounded-lg bg-white px-0.5 py-0 md:px-1">
                  {{ card.label }}
                </span>
              </span>
            </div>

            <div class="flex flex-col gap-y-0.5 px-1.5 py-1 md:gap-y-1 md:px-2 md:py-1.5">
              <div class="flex items-center gap-x-1">
                <img
                  v-if="card.icon === 'email'"
                  :src="`${baseImageUrl}/email.png`"
                  alt="Email"
                  class="size-3 object-contain"
                  draggable="false"
                >

                <template v-else>
                  <img
                    :src="`${baseImageUrl}/fb.png`"
                    alt="Facebook"
                    class="size-3 object-contain"
                    draggable="false"
                  >

                  <img
                    :src="`${baseImageUrl}/ig.png`"
                    alt="Instagram"
                    class="size-3 object-contain"
                    draggable="false"
                  >
                </template>
              </div>

              <span class="break-words text-[0.5rem] font-semibold leading-tight text-neutral-700 md:text-[0.625rem]">
                {{ card.title }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
